import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  LOGIN_ROUTE,
  setCurrentStep,
  setOtpSent,
  checkPhoneNumberExists,
  sendOTP,
  signUp,
  validateOtp,
} from "@hypexone/uicore";
import StepOne from "./StepOne";
import Button from "../../components/shared/Button/Button";
import SocialAuth from "../social-auth/SocialAuth";
import { useSignUpModal } from "../../context/SignUpModalContext";


interface SignUpFormProps {
  onSuccess?: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSignUpModalOpen } = useSignUpModal();

  const { otpSent, phoneNumberExists } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<number>();
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get("campaignId");
  const [campaignCode, setCampaignCode] = useState<string>(campaignId ?? "");
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const maxLength = 10;
    if (newValue.length <= maxLength && /^\d*$/.test(newValue)) {
      setPhoneNumber(newValue);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const showPasswordClickHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const rawCamp = campaignCode ?? "";
    const cleanCamp = rawCamp.trim().toLowerCase();
    setLoader(true);

    try {
      if (!otpSent && phoneNumber && agreedToTerms) {
        await dispatch(sendOTP("91" + phoneNumber)).unwrap();
      } else if (otpSent && otp && (!username || !password)) {
        await dispatch(
          validateOtp({ phoneNumber: `91${phoneNumber}`, otp })
        ).unwrap();
      } else if (otpSent && username && password) {
        await dispatch(
          signUp({
            username: username.toLowerCase(),
            fullName: username.toLowerCase(),
            emailId: email,
            password,
            phoneNumber: "91" + phoneNumber,
            otp,
            referralCode: "",
            campaignCode: cleanCamp,
          })
        ).unwrap();
        onSuccess?.();
        navigate("/home");
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const redirectToSignIn = () => {
    setSignUpModalOpen(false);
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    if (campaignId) {
      localStorage.setItem("campaignId", campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    dispatch(setCurrentStep(1));
    dispatch(setOtpSent(false));
  }, []);

  useEffect(() => {
    if (phoneNumber) {
      dispatch(checkPhoneNumberExists(phoneNumber));
    }
  }, [phoneNumber]);

  const isSubmitDisabled =
    !phoneNumber ||
    !agreedToTerms ||
    !isUsernameValid ||
    phoneNumberExists ||
    (otpSent && (!otp || !username || !password)) ||
    loader;

  return (
    <div className="w-full max-w-md overflow-y-auto">
      {/* Header (title/subtitle) */}
      <header className="flex flex-col justify-center items-center text-center">
        <h1 className="text-2xl font-bold text-black3">
          {langData?.["sign_up"]}
        </h1>
        <p className="text-xs text-black4 mt-1">
          {langData?.["create_your_account_txt"]}
        </p>
      </header>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col w-full text-sm font-medium"
        >
          <StepOne
            otpSent={otpSent}
            otp={otp}
            setPhoneNumber={setPhoneNumber}
            phoneNumber={phoneNumber}
            agreedToTerms={agreedToTerms}
            email={email}
            onEmailChange={handleEmailChange}
            onPhoneChange={handlePhoneChange}
            onTermsChange={handleTermsChange}
            onTermsClick={() => navigate("/legal/terms")}
            username={username}
            password={password}
            setOtp={setOtp}
            showPassword={showPassword}
            onUsernameChange={handleUsernameChange}
            onPasswordChange={handlePasswordChange}
            showPasswordClickHandler={showPasswordClickHandler}
            onUsernameValidityChange={setIsUsernameValid}
            campaignCode={campaignCode}
            onCampaignCodeChange={setCampaignCode}
          />

          {/* Submit button */}
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            loading={loader}
            className={`w-full py-3 mt-2 text-base font-semibold text-center rounded-xl min-h-[44px] shadow-lg transition-all duration-200 ${isSubmitDisabled
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-header-gradient text-goldenYellow hover:opacity-90"
              }`}
          >
            {!otpSent
              ? langData?.["send_otp"]
              : otpSent && (!username || !password)
                ? langData?.["validate_otp"]
                : langData?.["sign_up"]}
          </Button>

          {/* Social auth */}
          {(window.location.hostname.includes("crypto247") ||
            window.location.hostname.includes("anna555")) && (
              <div className="flex flex-col items-center text-black4 space-y-3 mt-2">
                <span className="text-sm">or continue with</span>
                <SocialAuth />
              </div>
            )}

          {/* Already have an account */}
          <div className="self-center mt-2 text-sm text-center text-black4">
            {langData?.["already_have_an_account"]}{" "}
            <button
              type="button"
              onClick={redirectToSignIn}
              className="underline font-semibold text-black4 hover:opacity-80 bg-transparent border-0 p-0 cursor-pointer"
            >
              {langData?.["sign_in"]}
            </button>
          </div>
        </form>
    </div>



  );
};

export default SignUpForm;
